import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingScreen';
import { AppIcon } from '@/components/AppIcon';
import { CategoryMark } from '@/components/CategoryMark';
import {
  subscribeToNotifications,
  unsubscribeFromNotifications
} from '@/lib/notifications';

interface FamilyInfo {
  id: string;
  name: string;
  inviteCode: string;
}

interface FamilyMember {
  user_id: string;
  display_name: string;
  photo_url?: string;
  role: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
}

const COLOR_OPTIONS = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#14B8A6', '#6366F1', '#6B7280'];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout, updateDisplayName, updatePhotoUrl } = useAuthStore();
  const { showToast, categories, setCategories } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [family, setFamily] = useState<FamilyInfo | null>(null);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [isPushEnabled, setIsPushEnabled] = useState(() => {
    // ローカルストレージから状態を復元
    if (typeof window !== 'undefined') {
      return localStorage.getItem('pushNotificationsEnabled') === 'true';
    }
    return false;
  });

  // カテゴリ編集モーダル
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryIcon, setCategoryIcon] = useState('');
  const [categoryColor, setCategoryColor] = useState('#6B7280');
  const [isSaving, setIsSaving] = useState(false);

  // 名前編集モーダル
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [editingName, setEditingName] = useState('');

  // 写真編集モーダル
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [editingPhotoUrl, setEditingPhotoUrl] = useState('');

  useEffect(() => {
    loadData();
    // 通知許可状態を確認して状態を同期
    if ('Notification' in window && Notification.permission === 'granted') {
      const savedState = localStorage.getItem('pushNotificationsEnabled') === 'true';
      setIsPushEnabled(savedState);
    } else {
      setIsPushEnabled(false);
      localStorage.setItem('pushNotificationsEnabled', 'false');
    }
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [familyRes, categoriesRes] = await Promise.all([
        api.getFamily().catch(() => ({ family: null, members: [] })),
        api.getCategories(),
      ]);
      setFamily(familyRes.family);
      setMembers(familyRes.members || []);
      setCategories(categoriesRes.categories);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const [isPushProcessing, setIsPushProcessing] = useState(false);

  const handleTogglePush = async () => {
    // 既に処理中なら無視
    if (isPushProcessing) return;

    const previousState = isPushEnabled;
    const newState = !isPushEnabled;

    // 楽観的に即座にUIを更新
    setIsPushEnabled(newState);
    setIsPushProcessing(true);

    try {
      if (newState) {
        // ON にする場合
        const currentPermission = 'Notification' in window ? Notification.permission : 'denied';
        if (currentPermission === 'denied') {
          // 即座に元に戻す
          setIsPushEnabled(previousState);
          showToast('ブラウザの設定から通知を許可してください', 'error');
          return;
        }

        const success = await subscribeToNotifications();
        if (success) {
          localStorage.setItem('pushNotificationsEnabled', 'true');
          showToast('プッシュ通知を有効にしました', 'success');
        } else {
          // 失敗したら元に戻す
          setIsPushEnabled(previousState);
          const permissionAfter = 'Notification' in window ? Notification.permission : 'denied';
          if (permissionAfter === 'denied') {
            showToast('ブラウザの設定から通知を許可してください', 'error');
          } else {
            showToast('通知の設定に失敗しました', 'error');
          }
        }
      } else {
        // OFF にする場合
        const success = await unsubscribeFromNotifications();
        if (success) {
          localStorage.setItem('pushNotificationsEnabled', 'false');
          showToast('プッシュ通知を無効にしました', 'success');
        } else {
          // 失敗したら元に戻す
          setIsPushEnabled(previousState);
          showToast('通知の設定に失敗しました', 'error');
        }
      }
    } finally {
      setIsPushProcessing(false);
    }
  };

  const handleLogout = async () => {
    if (!confirm('ログアウトしますか？')) return;

    try {
      await signOut(auth);
      logout();
      navigate('/login');
    } catch (error) {
      showToast('ログアウトに失敗しました', 'error');
    }
  };

  // カテゴリ編集モーダルを開く
  const openCategoryModal = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setCategoryName(category.name);
      setCategoryIcon(category.icon);
      setCategoryColor(category.color);
    } else {
      setEditingCategory(null);
      setCategoryName('');
      setCategoryIcon('');
      setCategoryColor('#6B7280');
    }
    setIsCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setEditingCategory(null);
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      showToast('カテゴリ名を入力してください', 'error');
      return;
    }

    setIsSaving(true);
    try {
      if (editingCategory) {
        // 更新
        await api.updateCategory(editingCategory.id, {
          name: categoryName,
          icon: categoryIcon,
          color: categoryColor,
        });
        setCategories(categories.map(c =>
          c.id === editingCategory.id
            ? { ...c, name: categoryName, icon: categoryIcon, color: categoryColor }
            : c
        ));
        showToast('カテゴリを更新しました', 'success');
      } else {
        // 新規作成
        const res = await api.createCategory({
          name: categoryName,
          icon: categoryIcon,
          color: categoryColor,
          sortOrder: categories.length,
        });
        setCategories([...categories, {
          id: res.id,
          name: categoryName,
          icon: categoryIcon,
          color: categoryColor,
          sortOrder: categories.length,
        }]);
        showToast('カテゴリを作成しました', 'success');
      }
      closeCategoryModal();
    } catch (error) {
      showToast('保存に失敗しました', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!editingCategory) return;
    if (!confirm('このカテゴリを削除しますか？')) return;

    setIsSaving(true);
    try {
      await api.deleteCategory(editingCategory.id);
      setCategories(categories.filter(c => c.id !== editingCategory.id));
      showToast('カテゴリを削除しました', 'success');
      closeCategoryModal();
    } catch (error) {
      showToast('削除に失敗しました', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pb-4">
      <div className="page-topbar">
        <h1 className="text-lg font-bold text-center py-4">設定</h1>
      </div>

      <div className="p-4 space-y-6">
        {/* ユーザー情報 */}
        <div className="card p-4">
          <h2 className="text-sm font-medium text-gray-500 mb-3">アカウント</h2>
          <div className="flex items-center gap-4">
            {user?.photoUrl ? (
              <img
                src={user.photoUrl}
                alt={user.displayName}
                className="w-14 h-14 rounded-full"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                <AppIcon name="user" size={23} />
              </div>
            )}
            <div>
              <p className="font-semibold">{user?.displayName}</p>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={() => {
              setEditingName(user?.displayName?.slice(0, 10) || '');
              setIsNameModalOpen(true);
            }}
            className="mt-3 text-sm text-primary-500 hover:text-primary-600 mr-4"
          >
            名前を変更
          </button>
          <button
            onClick={() => {
              setEditingPhotoUrl(user?.photoUrl || '');
              setIsPhotoModalOpen(true);
            }}
            className="mt-3 text-sm text-primary-500 hover:text-primary-600"
          >
            アイコンを変更
          </button>
        </div>

        {/* グループ情報 */}
        {family && (
          <div
            className="card p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/settings/group')}
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-medium text-gray-500 mb-1">グループ</h2>
                <p className="font-semibold">{family.name}</p>
                <p className="text-sm text-gray-500">{members.length}人のメンバー</p>
              </div>
              <span className="text-gray-400">→</span>
            </div>
          </div>
        )}

        {!family && (
          <div
            className="card p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/family/setup')}
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-medium text-gray-500 mb-1">グループ</h2>
                <p className="font-semibold text-primary-500">グループを作成</p>
              </div>
              <span className="text-gray-400">→</span>
            </div>
          </div>
        )}

        {/* カテゴリ管理 */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-gray-500">カテゴリ</h2>
            <button
              onClick={() => openCategoryModal()}
              className="text-primary-500 text-sm"
            >
              + 追加
            </button>
          </div>
          <div className="space-y-2">
            {categories.map((category) => (
              <div
                key={category.id}
                onClick={() => openCategoryModal(category)}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
              >
                <div className="flex items-center gap-2">
                  <CategoryMark name={category.name} color={category.color} size="sm" />
                  <span>{category.name}</span>
                </div>
                <span className="text-gray-400">→</span>
              </div>
            ))}
          </div>
        </div>

        {/* 通知設定 */}
        <div className="card p-4">
          <h2 className="text-sm font-medium text-gray-500 mb-3">通知</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">プッシュ通知</p>
              <p className="text-sm text-gray-500">精算リクエストなどを通知</p>
            </div>
            {isPushProcessing ? (
              <div className="w-11 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPushEnabled}
                  onChange={handleTogglePush}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
              </label>
            )}
          </div>
        </div>

        {/* デバッグ・トラブルシューティング */}
        <div className="card p-4">
          <h2 className="text-sm font-medium text-gray-500 mb-3">デバッグ・トラブルシューティング</h2>
          <button
            onClick={async () => {
              if (!confirm('アプリのキャッシュを削除して修復しますか？\n（ログイン状態は保持されますが、画面が再読み込みされます）')) {
                return;
              }

              try {
                // Service Workerの解除
                if ('serviceWorker' in navigator) {
                  const registrations = await navigator.serviceWorker.getRegistrations();
                  for (const registration of registrations) {
                    await registration.unregister();
                  }
                }

                // キャッシュの削除
                if ('caches' in window) {
                  const keys = await caches.keys();
                  for (const key of keys) {
                    await caches.delete(key);
                  }
                }

                showToast('リセットしました。再読み込みします...', 'success');

                // 少し待ってからリロード
                setTimeout(() => {
                  window.location.reload();
                }, 1000);
              } catch (error) {
                console.error('Reset failed:', error);
                showToast('リセットに失敗しました', 'error');
              }
            }}
            className="w-full p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 flex items-center gap-2"
          >
            <AppIcon name="refresh" size={17} />
            <span>アプリデータをリセットして修復</span>
          </button>
        </div>

        {/* ログアウト */}
        <button
          onClick={handleLogout}
          className="btn btn-danger btn-lg w-full"
        >
          ログアウト
        </button>

        <p className="text-center text-sm text-gray-400">バージョン 0.0.3</p>
      </div>

      {/* カテゴリ編集モーダル */}
      {isCategoryModalOpen && (
        <>
          <div className="modal-overlay" onClick={closeCategoryModal} />
          <div className="modal-content p-6">
            <h2 className="text-lg font-bold mb-4">
              {editingCategory ? 'カテゴリ編集' : '新規カテゴリ'}
            </h2>

            <div className="space-y-4">
              {/* カテゴリ名 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  カテゴリ名
                </label>
                <input
                  type="text"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  className="input"
                  placeholder="例: 食費"
                />
              </div>

              {/* 色選択 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  色
                </label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setCategoryColor(color)}
                      className={`w-10 h-10 rounded-lg border-2 ${categoryColor === color
                        ? 'border-gray-900 scale-110'
                        : 'border-transparent'
                        }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              {editingCategory && (
                <button
                  onClick={handleDeleteCategory}
                  disabled={isSaving}
                  className="btn btn-danger btn-md"
                >
                  削除
                </button>
              )}
              <button
                onClick={closeCategoryModal}
                className="btn btn-secondary btn-md flex-1"
              >
                キャンセル
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={isSaving}
                className="btn btn-primary btn-md flex-1"
              >
                {isSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* 名前編集モーダル */}
      {isNameModalOpen && (
        <>
          <div className="modal-overlay" onClick={() => setIsNameModalOpen(false)} />
          <div className="modal-content p-6">
            <h2 className="text-lg font-bold mb-4">名前を変更</h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                表示名（10文字まで）
              </label>
              <input
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value.slice(0, 10))}
                placeholder="名前を入力"
                className="input"
                maxLength={10}
              />
              <p className="mt-1 text-xs text-gray-500">
                グループ内で表示される名前です
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsNameModalOpen(false)}
                className="btn btn-secondary btn-md flex-1"
              >
                キャンセル
              </button>
              <button
                onClick={async () => {
                  if (!editingName.trim()) {
                    showToast('名前を入力してください', 'error');
                    return;
                  }
                  setIsSaving(true);
                  try {
                    await api.updateUserProfile({ displayName: editingName.trim() });
                    updateDisplayName(editingName.trim());
                    setIsNameModalOpen(false);
                    showToast('名前を変更しました', 'success');
                  } catch (error: any) {
                    showToast(error.message || '名前の変更に失敗しました', 'error');
                  } finally {
                    setIsSaving(false);
                  }
                }}
                disabled={isSaving || !editingName.trim()}
                className="btn btn-primary btn-md flex-1"
              >
                {isSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* 写真編集モーダル */}
      {isPhotoModalOpen && (
        <>
          <div className="modal-overlay" onClick={() => setIsPhotoModalOpen(false)} />
          <div className="modal-content p-6">
            <h2 className="text-lg font-bold mb-4">アイコンを選択</h2>

            <div className="mb-6">
              {/* 現在の選択 */}
              <div className="flex justify-center mb-4">
                {editingPhotoUrl ? (
                  <img
                    src={editingPhotoUrl}
                    alt="選択中"
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-primary-500"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 ring-2 ring-primary-500">
                    <AppIcon name="user" size={25} />
                  </div>
                )}
              </div>

              {/* アイコン選択グリッド */}
              <div className="grid grid-cols-4 gap-3">
                {/* 未設定オプション */}
                <button
                  onClick={() => setEditingPhotoUrl('')}
                  className={`w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-xl transition-all ${!editingPhotoUrl ? 'ring-2 ring-primary-500 scale-110' : 'hover:scale-105'
                    }`}
                >
                  <AppIcon name="user" size={22} />
                </button>
                {/* アバターアイコン */}
                {['man', 'woman', 'boy', 'girl', 'dog', 'cat', 'hamster', 'bear'].map((name) => (
                  <button
                    key={name}
                    onClick={() => setEditingPhotoUrl(`/avatars/${name}.svg`)}
                    className={`w-14 h-14 rounded-full overflow-hidden transition-all ${editingPhotoUrl === `/avatars/${name}.svg` ? 'ring-2 ring-primary-500 scale-110' : 'hover:scale-105'
                      }`}
                  >
                    <img
                      src={`/avatars/${name}.svg`}
                      alt={name}
                      className="w-full h-full"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsPhotoModalOpen(false)}
                className="btn btn-secondary btn-md flex-1"
              >
                キャンセル
              </button>
              <button
                onClick={async () => {
                  setIsSaving(true);
                  try {
                    const photoUrl = editingPhotoUrl || null;
                    await api.updateUserProfile({ photoUrl });
                    updatePhotoUrl(photoUrl);
                    setIsPhotoModalOpen(false);
                    showToast('アイコンを変更しました', 'success');
                  } catch (error: any) {
                    showToast(error.message || 'アイコンの変更に失敗しました', 'error');
                  } finally {
                    setIsSaving(false);
                  }
                }}
                disabled={isSaving}
                className="btn btn-primary btn-md flex-1"
              >
                {isSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
